import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Divider,
  Spinner,
  Circle,
  Link,
  Collapse,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaShieldAlt } from "react-icons/fa";
import { getBid } from "../../lib/Auth";
import VerifyEligibilityModal from "./VerifyEligibilityModal";
import EligibilityDetailsDrawer, { DownloadPdf, PmsStateLine } from "./EligibilityDetailsDrawer";
import {
  ivApi,
  isOk,
  mergePayerEntries,
  statusLine,
  checkActions,
  latestFinished,
  pendingVerification,
  pendingVerifications,
  pendingLabel,
  settlingAfterDone,
  SETTLE_WINDOW_MS,
  SETTLE_RECENT_MS,
  errorMessage,
  formatDate,
  annualMaximum,
  amountLine,
  watchVerification,
  IV_FINISHED_EVENT,
  SETTINGS_PATH,
  SETUP_PATH,
  WORKLIST_PATH,
} from "./insuranceVerification";

/**
 * InsuranceEligibilityCard — the Inbox patient panel's Insurance card (SS-3944).
 *
 * The patient's current insurance (insurer, member id, subscriber or dependent), the
 * status line (Verified Active · date, Inactive · date, Not verified, Stale), the two
 * checks labelled by the insurer's access with their credit cost, a running breakdown's
 * state (polled; the app-level InsuranceVerificationNotifier raises the Inbox notification
 * when it is done, whichever conversation is open), the latest result with its PDF and PMS
 * state, and the history of past checks. Office staff only: the API refuses anyone else,
 * and then the card renders nothing.
 */

const POLL_MS = 20000;
// a done result's answers and PMS save usually take seconds: asked more often while staff wait on them
const SETTLE_POLL_MS = 5000;

// what the result drawer shows of a check: it reloads when any of it changes
const drawerKey = (v) => (v ? [v.status, v.answers?.status || "", v.pms_write?.state || ""].join("|") : undefined);

const BADGE = {
  unverified: { label: "Not verified", bg: "#E5E5EF", fg: "#64728C", dot: "#A0A8BC" },
  active: { label: "Active", bg: "#E2F3F3", fg: "#154043", dot: "#1FA971" },
  inactive: { label: "Inactive", bg: "#FBE9E9", fg: "#B3261E", dot: "#D9352A" },
  stale: { label: "Stale", bg: "#FCEFD8", fg: "#8A5A00", dot: "#E0991A" },
  pending: { label: "Checking…", bg: "#E2EEFB", fg: "#1B5FB0", dot: "#1B5FB0" },
};

function StatusBadge({ kind }) {
  const cfg = BADGE[kind] || BADGE.unverified;
  return (
    <Flex align="center" gap="6px" bg={cfg.bg} borderRadius="full" px="8px" py="3px" data-testid={`iv-badge-${kind}`}>
      <Circle size="8px" bg={cfg.dot} />
      <Text fontSize="12px" fontWeight="600" lineHeight="140%" color={cfg.fg}>{cfg.label}</Text>
    </Flex>
  );
}

function DataRow({ label, value, valueColor }) {
  const labelColor = useColorModeValue("#64728C", "#A9B1C2");
  const defaultValue = useColorModeValue("#11131B", "#F9F9FB");
  return (
    <Flex justify="space-between" align="center" w="100%" gap="2">
      <Text fontSize="13px" lineHeight="140%" color={labelColor}>{label}</Text>
      <Text fontSize="13px" fontWeight="600" lineHeight="140%" color={valueColor || defaultValue} textAlign="right">{value}</Text>
    </Flex>
  );
}

const inNetworkFirst = (rows) => [...(rows || [])].sort((a, b) => Number(b.network?.status === "InNetwork") - Number(a.network?.status === "InNetwork"));

export default function InsuranceEligibilityCard({ patientId, patientName = "", width = "100%", pollMs = POLL_MS, settlePollMs = SETTLE_POLL_MS }) {
  const [settings, setSettings] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [payer, setPayer] = useState(null);
  const [portalState, setPortalState] = useState(undefined);
  const [offer, setOffer] = useState(undefined);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetType, setSheetType] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const patientRef = useRef(patientId);
  patientRef.current = patientId;

  const cardBg = useColorModeValue("#F9F9FB", "#212330");
  const titleColor = useColorModeValue("#11131B", "#F9F9FB");
  const subColor = useColorModeValue("#64728C", "#A9B1C2");
  const dividerColor = useColorModeValue("#E5E5EF", "#3A3D4D");
  const linkColor = "#8E4997";
  const primary = "#154043";
  const bid = getBid();

  const listSeq = useRef(0);
  const loadVerifications = useCallback(async () => {
    const seq = ++listSeq.current;
    const r = await ivApi.listVerifications(patientId);
    // a late answer for the previous patient never lands on this one's card
    if (patientRef.current !== patientId) return null;
    // nor an older request's (e.g. a poll that started before a check was submitted) over a newer one
    if (seq !== listSeq.current) return null;
    if (isOk(r)) setVerifications(r.data || []);
    return isOk(r) ? r.data || [] : null;
  }, [patientId]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setHidden(false);
    setPayer(null);
    setPortalState(undefined);
    setOffer(undefined);
    setVerifications([]);
    setPrefill(null);
    (async () => {
      const s = await ivApi.getSettings();
      if (!live) return;
      if (!isOk(s)) {
        setHidden(true);
        setLoading(false);
        return;
      }
      setSettings(s.data);
      if (!s.data?.enabled) {
        setLoading(false);
        return;
      }
      const [p] = await Promise.all([ivApi.getPrefill(patientId), loadVerifications()]);
      if (!live) return;
      if (!isOk(p)) {
        // not this practice's patient (e.g. a team conversation): nothing to verify here
        setHidden(true);
        setLoading(false);
        return;
      }
      const pre = p.data;
      setPrefill(pre);
      // the API's own breakdown decision and portal state, for every staff member (SS-3939's prefill)
      const apiOffer = pre?.breakdown && typeof pre.breakdown === "object" ? pre.breakdown : undefined;
      setOffer(apiOffer);
      if (pre?.payerId) {
        const found = await ivApi.searchPayers(pre.payerId);
        // one payer id can stand for several network names (SS-4455): named as the API names the patient's
        // insurer (payer_name: the entry the chart's payer id is, else the chart's carrier); an API that
        // does not name it, after the chart's carrier here
        const merged = isOk(found)
          ? mergePayerEntries((found.data || []).filter((x) => x.commonPayerId === pre.payerId), { carrierName: pre.carrier_name })
          : null;
        const match = merged && pre.payer_name ? { ...merged, name: pre.payer_name } : merged;
        if (!live) return;
        setPayer(match || { commonPayerId: pre.payerId, name: pre.payer_name || pre.carrier_name || pre.payerId, access: "unknown", portals: [] });
        if (!apiOffer && match && (match.access === "portal_only" || match.access === "direct_and_portal")) {
          // an API without the prefill's offer: practice admins can read the portals; for other staff the API decides on submit
          const portals = await ivApi.getPortals();
          if (live && isOk(portals)) {
            const states = (portals.data || []).filter((x) => (match.portals || []).includes(x.portalId)).map((x) => x.state);
            setPortalState(states.includes("connected") ? "connected" : states.includes("needs_attention") ? "needs_attention" : "not_connected");
          }
        }
      }
      if (live) setLoading(false);
    })();
    return () => { live = false; };
  }, [patientId, loadVerifications, reloadKey]);

  // the patient's insurer as this card names it; a check of that payer is named so wherever it shows
  // (the result drawer, the Inbox notification), not by the name the API stored for the payer id (SS-4455).
  // Known from the prefill already while the payer itself is still being looked up
  const insurer = payer?.name || prefill?.payer_name || prefill?.carrier_name || "";
  const insurerId = payer?.commonPayerId || prefill?.payerId || "";
  const payerNameOf = (x) => (insurerId && x?.payer?.common_payer_id === insurerId && insurer) || x?.payer?.name || "";

  // a running check or breakdown is followed until it is done; the app-level notifier tells the Inbox
  const pending = pendingVerification(verifications);
  const pendingId = pending?._id || null;
  const pendingStatus = pending?.status || null;
  // every running check of this patient is followed (another workstation may have started one too);
  // the list still on screen right after the conversation changed is the previous patient's: never named after this one
  const running = pendingVerifications(verifications)
    .filter((x) => !x.patient_id || String(x.patient_id) === String(patientId))
    .map((x) => ({ id: x._id, type: x.type, status: x.status, payerName: payerNameOf(x) }));
  const runningKey = JSON.stringify(running);
  const runningIds = useRef([]);
  runningIds.current = running.map((x) => x.id);
  // followed once the card knows the patient's insurer, so a check is never announced by another entry's
  // name; until then kept here, so a card left first (another conversation, or gone) still has its checks
  // followed, under the name known so far (SS-4455)
  const notYetFollowed = useRef(null);
  useEffect(() => {
    if (loading) {
      notYetFollowed.current = { bid, list: JSON.parse(runningKey), patientName };
      return;
    }
    notYetFollowed.current = null;
    for (const x of JSON.parse(runningKey)) watchVerification(bid, { ...x, patientName });
  }, [bid, runningKey, patientName, loading]);
  useEffect(() => () => {
    const left = notYetFollowed.current;
    notYetFollowed.current = null;
    if (left) for (const x of left.list) watchVerification(left.bid, { ...x, patientName: left.patientName });
  }, [patientId]);
  useEffect(() => {
    // followed once the card has loaded: registered first (above), so its end is never missed (SS-4455)
    if (!pendingId || loading) return undefined;
    let live = true;
    const t = setInterval(async () => {
      const r = await ivApi.getVerification(pendingId);
      // an answer that arrives after this check stopped being followed here changes nothing
      if (!live || !isOk(r)) return;
      if (r.data.status === "done" || r.data.status === "failed") {
        await loadVerifications();
      } else if (r.data.status !== pendingStatus) {
        // a late "still running" answer never replaces a result already shown
        setVerifications((list) => list.map((x) => (x._id === r.data._id && !["done", "failed"].includes(x.status) ? { ...x, ...r.data } : x)));
      }
    }, pollMs);
    // the notifier saw one of this patient's checks finish first: show the result now
    const onFinished = (e) => {
      if (e?.detail?.id === pendingId || runningIds.current.includes(e?.detail?.id)) loadVerifications();
    };
    window.addEventListener(IV_FINISHED_EVENT, onFinished);
    return () => {
      live = false;
      clearInterval(t);
      window.removeEventListener(IV_FINISHED_EVENT, onFinished);
    };
  }, [pendingId, pendingStatus, loadVerifications, pollMs, loading]);

  // a done check's answers and PMS save finish after it: followed until they settle, so the card's
  // PMS state and the drawer open on the result (keyed on what it shows) catch up without a reload.
  // Each is followed for SETTLE_WINDOW_MS from when this card first saw it settling, by its own clock
  // (the API's may differ); results that finished long before are stuck, not followed at all
  const settleSeen = useRef(new Map());
  const settlingKey = verifications.filter((x) => settlingAfterDone(x, { windowMs: SETTLE_RECENT_MS }))
    .map((x) => `${x._id}|${x.answers?.status || ""}|${x.pms_write?.state || ""}`).join(",");
  useEffect(() => {
    // as the running checks' poll: not before the card has loaded and registered them (SS-4455)
    if (!settlingKey || loading) return undefined;
    const now = Date.now();
    const ids = settlingKey.split(",").map((k) => k.split("|")[0]);
    for (const id of ids) if (!settleSeen.current.has(id)) settleSeen.current.set(id, now);
    const until = Math.max(...ids.map((id) => settleSeen.current.get(id))) + SETTLE_WINDOW_MS;
    if (now >= until) return undefined;
    let busy = false;
    const t = setInterval(async () => {
      // the deadline holds even when no answer comes back to re-render the card
      if (Date.now() >= until) {
        clearInterval(t);
        return;
      }
      if (busy) return; // one request at a time: a slow answer is never overtaken by an older one
      busy = true;
      try {
        await loadVerifications();
      } finally {
        busy = false;
      }
    }, settlePollMs);
    return () => clearInterval(t);
  }, [settlingKey, loadVerifications, settlePollMs, loading]);

  if (hidden) return null;

  const shell = (children, kind) => (
    <Box bg={cardBg} borderRadius="8px" px="12px" py="11px" w={width} data-testid="iv-card">
      <Flex direction="column" gap="8px">
        <Flex justify="space-between" align="center" w="100%">
          <Text fontSize="14px" fontWeight="600" lineHeight="140%" color={titleColor}>Insurance</Text>
          {kind && <StatusBadge kind={kind} />}
        </Flex>
        {children}
      </Flex>
    </Box>
  );

  if (loading) return shell(<Flex justify="center" py="2"><Spinner size="sm" /></Flex>);

  if (!settings?.enabled) {
    return shell(
      <>
        <Text fontSize="13px" color={subColor}>Insurance verification is not set up for this practice.</Text>
        <Link href={`${SETUP_PATH}?bid=${bid}`} fontSize="13px" fontWeight="600" color={linkColor} data-testid="iv-setup-link">Set up insurance verification</Link>
      </>
    );
  }

  // the status and benefits are the CURRENT insurer's: an earlier insurer's result says nothing here
  // (an insurer not matched to a payer yet has none: past checks stay in the history only)
  const current = payer?.commonPayerId
    ? (verifications || []).filter((x) => x.payer?.common_payer_id === payer.commonPayerId)
    : [];
  const status = statusLine(current, { staleDays: settings?.stale_days });
  const latest = latestFinished(current);
  const latestDone = (current || []).filter((x) => x.status === "done")
    .sort((a, b) => new Date(b.completed || b.created) - new Date(a.completed || a.created))[0];
  const actions = checkActions({ payer, portalState, offer, verifications, credits: settings?.credits });
  const failure = latest?.status === "failed" ? errorMessage(latest) : null;
  const questionsOn = (settings?.questions || []).some((q) => q && q.enabled !== false);
  const relationship = prefill?.dependent ? "Dependent" : prefill?.subscriber ? "Subscriber" : "";
  const memberId = prefill?.subscriber?.id || prefill?.member_id || "";
  const s = latestDone?.summary;
  const max = annualMaximum(s?.maximums);
  const ded = inNetworkFirst((s?.deductibles || []).filter((d) => d.coverage_level === "Individual"))[0] || inNetworkFirst(s?.deductibles)[0];

  const onSubmitted = async (v) => {
    setSheetType(null);
    // followed from the moment it is accepted, whatever happens to this card or its next list
    if (v?._id && ["requested", "in_progress", "retrying"].includes(v.status)) {
      watchVerification(bid, { id: v._id, type: v.type, status: v.status, patientName, payerName: payerNameOf(v) });
    }
    // checked against another insurer than the card's (staff corrected it): reload the insurance itself
    if (v?.payer?.common_payer_id && v.payer.common_payer_id !== payer?.commonPayerId) setReloadKey((k) => k + 1);
    else await loadVerifications();
    if (v?.status === "done" || v?.status === "failed") setDrawerId(v._id);
  };

  return (
    <>
      {shell(
        <>
          <Box data-testid="iv-current-insurance">
            <Flex justify="space-between" align="center" w="100%" gap="2">
              <Text fontSize="13px" fontWeight="500" color={titleColor} lineHeight="140%">{insurer || "No insurance on file"}</Text>
              {relationship && <Text fontSize="12px" color={subColor}>{relationship}</Text>}
            </Flex>
            {memberId && <Text fontSize="12px" color={subColor}>Member ID {memberId}</Text>}
          </Box>
          <Text fontSize="12px" fontWeight="600" color={status.kind === "inactive" ? "#B3261E" : status.kind === "stale" ? "#8A5A00" : subColor} data-testid="iv-status-line">
            {status.label}
          </Text>

          {pending && (
            <Flex align="center" gap="8px" data-testid="iv-pending">
              <Spinner size="sm" color={primary} thickness="2px" speed="0.7s" />
              <Text fontSize="13px" color={subColor}>{pendingLabel(pending)}</Text>
            </Flex>
          )}

          {latestDone && s && latest?._id === latestDone._id && (
            <>
              <Divider borderColor={dividerColor} />
              {s.plan_name && <DataRow label="Plan" value={[s.plan_name, s.plan_type].filter(Boolean).join(" · ")} />}
              {max && <DataRow label="Annual max" value={amountLine(max)} />}
              {ded && <DataRow label="Deductible" value={amountLine(ded)} />}
              <Flex justify="space-between" align="center" wrap="wrap" gap="2">
                <Text as="button" fontSize="13px" fontWeight="600" color={linkColor} onClick={() => setDrawerId(latestDone._id)} data-testid="iv-view-result">
                  View result
                </Text>
                <DownloadPdf v={latestDone} hasQuestions={questionsOn} size="xs" />
              </Flex>
              <PmsStateLine v={latestDone} onRetried={() => loadVerifications()} />
            </>
          )}

          {failure && (
            <Box bg={failure.kind === "unavailable" ? "#E2EEFB" : "#FBE9E9"} borderRadius="6px" p="2" data-testid="iv-error-box">
              <Text fontSize="13px" fontWeight="700" color={failure.kind === "unavailable" ? "#1B5FB0" : "#B3261E"}>{failure.title}</Text>
              <Text fontSize="12px" color={titleColor}>{failure.text}</Text>
              <Text fontSize="11px" color={subColor} mt="1">{failure.charged ? "Charged" : "Not charged"}</Text>
              {failure.action === "settings" ? (
                <Link href={`${SETTINGS_PATH}?bid=${bid}`} fontSize="12px" fontWeight="600" color={linkColor}>Open portal settings</Link>
              ) : pending ? null : ( // a check already running: no second (paid) one from here
                <Text as="button" fontSize="12px" fontWeight="600" color={linkColor} onClick={() => setSheetType(latest.type === "breakdown" ? "breakdown" : "eligibility")}
                  data-testid="iv-error-retry">
                  {failure.action === "retry" ? "Retry" : "Edit & retry"}
                </Text>
              )}
            </Box>
          )}

          <Button size="sm" bg={primary} color="#F9F9FB" _hover={{ bg: "#123536" }} borderRadius="8px"
            leftIcon={<FaShieldAlt size={13} />} onClick={() => setSheetType("eligibility")} isDisabled={!!pending}
            whiteSpace="normal" h="auto" py="2" data-testid="iv-check">
            {actions.check.label}
          </Button>
          {actions.breakdown && (
            <Box>
              <Button size="sm" variant="outline" borderColor={primary} color={primary} borderRadius="8px" w="100%"
                whiteSpace="normal" h="auto" py="2"
                isDisabled={actions.breakdown.disabled || !!pending} onClick={() => setSheetType("breakdown")} data-testid="iv-breakdown">
                {actions.breakdown.label}
              </Button>
              {actions.breakdown.disabled && actions.breakdown.reason && (
                <Text fontSize="12px" color={subColor} mt="1" data-testid="iv-breakdown-reason">{actions.breakdown.reason}</Text>
              )}
            </Box>
          )}
          {actions.connect && (
            <Link href={`${actions.connect.href}&bid=${bid}`} fontSize="13px" fontWeight="600" color={linkColor} data-testid="iv-connect-portal">
              {actions.connect.label}
            </Link>
          )}

          {verifications.length > 0 && (
            <Box>
              <Text as="button" fontSize="12px" color={linkColor} onClick={() => setShowHistory((x) => !x)} data-testid="iv-history-toggle">
                {showHistory ? "Hide" : "Show"} past checks ({verifications.length})
              </Text>
              <Collapse in={showHistory} animateOpacity>
                <Box mt="1" data-testid="iv-history">
                  {verifications.map((x) => (
                    <Flex key={x._id} justify="space-between" fontSize="12px" py="1" gap="2">
                      <Text color={subColor}>{formatDate(x.completed || x.created)} · {x.type === "breakdown" ? "Breakdown" : "Check"}</Text>
                      <Text as="button" color={linkColor} onClick={() => setDrawerId(x._id)}>
                        {x.status === "done" ? (x.summary?.coverage_status || "Done") : x.status === "failed" ? (errorMessage(x)?.title || "Failed") : pendingLabel(x)}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}

          <Flex gap="3" fontSize="12px">
            <Link href={`${WORKLIST_PATH}?bid=${bid}`} color={linkColor}>Appointment worklist</Link>
            {/* the settings are changed by practice admins only (SS-4455): not offered to the others */}
            {settings?.can_manage !== false && <Link href={`${SETTINGS_PATH}?bid=${bid}`} color={linkColor}>Settings</Link>}
          </Flex>
        </>,
        pending ? "pending" : status.kind,
      )}

      {sheetType && (
        <VerifyEligibilityModal
          isOpen={!!sheetType}
          onClose={() => setSheetType(null)}
          patientId={patientId}
          type={sheetType}
          prefill={prefill}
          settings={settings}
          payer={payer}
          onSubmitted={onSubmitted}
        />
      )}
      {drawerId && (
        <EligibilityDetailsDrawer
          isOpen={!!drawerId}
          onClose={() => setDrawerId(null)}
          verificationId={drawerId}
          statusKey={drawerKey(verifications.find((x) => x._id === drawerId))}
          payerNames={payer?.commonPayerId && insurer ? { [payer.commonPayerId]: insurer } : undefined}
          settings={settings}
          onChanged={() => loadVerifications()}
          onRecheck={pending ? undefined : (type) => { setDrawerId(null); setSheetType(type === "breakdown" ? "breakdown" : "eligibility"); }}
        />
      )}
    </>
  );
}
